import { getStudyScope, isStudyScopeCurrent, studyStorageKey, type StudyScope } from './studyStorage';
import { remoteAccountDocument, saveAccountDocument } from './accountPersistence';

export type ChatMessageRole = 'user' | 'assistant';
export type ChatConversationSource = 'main-tutor' | 'lesson';

export interface ChatMessage {
  id: string;
  role: ChatMessageRole;
  content: string;
  createdAt: string;
}

export interface LessonChatContext {
  lessonId: string;
  lessonTitle: string;
  subject?: string;
  topic?: string;
  summary?: string;
  keyPoints?: string[];
  narration?: string;
}

export interface ChatConversation {
  id: string;
  title: string;
  source: ChatConversationSource;
  lessonContext?: LessonChatContext;
  messages: ChatMessage[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateChatConversationInput {
  title?: string;
  source?: ChatConversationSource;
  lessonContext?: LessonChatContext;
  messages?: ChatMessage[];
}

export interface UpdateChatConversationInput {
  title?: string;
  source?: ChatConversationSource;
  lessonContext?: LessonChatContext;
  messages?: ChatMessage[];
}

const STORAGE_PREFIX = 'lavyn_chat_conversations';
function ownerFor(userId?: string | null, captured?: StudyScope): StudyScope | null {
  const owner = captured || getStudyScope();
  return owner.ready && owner.ownerId === (userId?.trim() || null) ? owner : null;
}

function storageKey(owner: StudyScope): string {
  // Preserve account-owned keys; never import the unowned legacy guest key.
  return owner.ownerId ? `${STORAGE_PREFIX}:${owner.ownerId}` : studyStorageKey('chat_conversations', owner);
}

function makeId(prefix: string): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}-${crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function readOwnedConversations(owner: StudyScope | null): ChatConversation[] {
  if (typeof window === 'undefined' || !owner) return [];
  const remote = remoteAccountDocument(owner.ownerId, 'chats');
  if (remote && Array.isArray(remote.payload)) return remote.payload as ChatConversation[];

  try {
    const raw = (owner.ownerId ? window.localStorage : window.sessionStorage).getItem(storageKey(owner));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed as ChatConversation[] : [];
  } catch {
    return [];
  }
}

function readConversations(userId?: string | null): ChatConversation[] {
  const owner = ownerFor(userId);
  return owner && isStudyScopeCurrent(owner) ? readOwnedConversations(owner) : [];
}

function writableOwner(userId?: string | null, captured?: StudyScope): StudyScope | null {
  const owner = ownerFor(userId, captured);
  // A late account reply can be saved to its original account. Retired guest
  // sessions must not be recreated after sign-out or an account switch.
  return owner && (owner.ownerId || isStudyScopeCurrent(owner)) ? owner : null;
}

function writeConversations(conversations: ChatConversation[], owner: StudyScope | null): void {
  if (typeof window === 'undefined' || !owner) return;
  (owner.ownerId ? window.localStorage : window.sessionStorage).setItem(storageKey(owner), JSON.stringify(conversations));
  void saveAccountDocument(owner.ownerId, 'chats', conversations).catch(() => {});
}

function chronologicalMessages(messages: ChatMessage[]): ChatMessage[] {
  return [...messages].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );
}

/**
 * Creates a short, useful title from the first learner message in a chat.
 */
export function generateChatTitle(messages: readonly ChatMessage[] | string): string {
  const content = typeof messages === 'string'
    ? messages
    : messages.find((message) => message.role === 'user')?.content ?? '';
  const normalized = content.replace(/\s+/g, ' ').trim();

  if (!normalized) return 'New AI chat';
  if (normalized.length <= 56) return normalized;
  return `${normalized.slice(0, 53).trimEnd()}…`;
}

export function listChatConversations(userId?: string | null): ChatConversation[] {
  return readConversations(userId).sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
  );
}

export function getChatConversation(
  conversationId: string,
  userId?: string | null,
): ChatConversation | null {
  return readConversations(userId).find((conversation) => conversation.id === conversationId) ?? null;
}

export function createChatConversation(
  input: CreateChatConversationInput = {},
  userId?: string | null,
  captured?: StudyScope,
): ChatConversation {
  const now = new Date().toISOString();
  const messages = chronologicalMessages(input.messages ?? []);
  const conversation: ChatConversation = {
    id: makeId('chat'),
    title: input.title?.trim() || generateChatTitle(messages),
    source: input.source ?? 'main-tutor',
    ...(input.lessonContext ? { lessonContext: input.lessonContext } : {}),
    messages,
    createdAt: now,
    updatedAt: now,
  };

  const owner = writableOwner(userId, captured);
  writeConversations([conversation, ...readOwnedConversations(owner)], owner);
  return conversation;
}

export function updateChatConversation(
  conversationId: string,
  updates: UpdateChatConversationInput,
  userId?: string | null,
): ChatConversation | null {
  const conversations = readConversations(userId);
  const index = conversations.findIndex((conversation) => conversation.id === conversationId);
  if (index === -1) return null;

  const updated: ChatConversation = {
    ...conversations[index],
    ...updates,
    title: updates.title === undefined ? conversations[index].title : updates.title.trim() || conversations[index].title,
    messages: updates.messages === undefined
      ? conversations[index].messages
      : chronologicalMessages(updates.messages),
    updatedAt: new Date().toISOString(),
  };
  conversations[index] = updated;
  writeConversations(conversations, writableOwner(userId));
  return updated;
}

export function deleteChatConversation(conversationId: string, userId?: string | null): boolean {
  const conversations = readConversations(userId);
  const remaining = conversations.filter((conversation) => conversation.id !== conversationId);
  if (remaining.length === conversations.length) return false;

  writeConversations(remaining, writableOwner(userId));
  return true;
}

export function appendChatMessage(
  conversationId: string,
  message: Omit<ChatMessage, 'id' | 'createdAt'> & Partial<Pick<ChatMessage, 'id' | 'createdAt'>>,
  userId?: string | null,
  captured?: StudyScope,
): ChatConversation | null {
  const owner = writableOwner(userId, captured);
  const conversations = readOwnedConversations(owner);
  const index = conversations.findIndex((conversation) => conversation.id === conversationId);
  if (index === -1) return null;

  const appendedMessage: ChatMessage = {
    id: message.id ?? makeId('message'),
    role: message.role,
    content: message.content,
    createdAt: message.createdAt ?? new Date().toISOString(),
  };
  const current = conversations[index];
  const updated: ChatConversation = {
    ...current,
    title: current.title === 'New AI chat' && appendedMessage.role === 'user'
      ? generateChatTitle(appendedMessage.content)
      : current.title,
    messages: [...current.messages, appendedMessage],
    updatedAt: new Date().toISOString(),
  };

  conversations[index] = updated;
  writeConversations(conversations, owner);
  return updated;
}